// The arithmetic behind the Youth Fellowship summary. A fellowship is a rule
// (age band + gender) over the church register, so its members are counted
// from the register here too; programmes are the fellowship's own records.

import {
  countBy, totalBy, sumOf, lastMonths, upcoming, inYear, amountOf, dateOf, isoOf, yearsSince, daysUntil
} from '../../utils/summaryStats';
import type { NamedCount, NamedTotal, MonthBucket } from '../../utils/summaryStats';

export interface FellowshipLike {
  name?: string;
  ageFrom?: number | null;
  ageTo?: number | null;
  /** Male / Female / Both — a blank (or "Mixed") takes everyone. */
  gender?: string;
  status?: string;
}

export interface PersonLike {
  id?: number | string;
  name?: string;
  gender?: string;
  dateOfBirth?: string;
  occupation?: string;
  /** Life status in the register: Alive / Deceased / Transferred… */
  status?: string;
}

export interface ProgramLike {
  fellowshipName?: string;
  date?: string;
  title?: string;
  progType?: string;
  attendance?: unknown;
  budget?: unknown;
  status?: string;
}

const isVoid = (r: { status?: string }) => String(r.status || '').toUpperCase() === 'VOID';

/** Shown when a legacy programme predates the fellowship field (as on the page). */
const FELLOWSHIP_FALLBACK = 'Youth Fellowship';

const bound = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

/** Whether a register member falls inside a fellowship's rule, on the given day. */
export const fitsFellowship = (g: FellowshipLike, m: PersonLike, now: Date = new Date()): boolean => {
  const wants = /^(male|female)$/i.test(String(g.gender || '').trim()) ? String(g.gender).trim().toLowerCase() : null;
  if (wants && String(m.gender || '').toLowerCase() !== wants) return false;
  const from = bound(g.ageFrom); const to = bound(g.ageTo);
  if (from === null && to === null) return true;
  const age = yearsSince(m.dateOfBirth, now);
  if (age === null || age < 0) return false;
  return (from === null || age >= from) && (to === null || age <= to);
};

/** Studying / Working / Not working / Not recorded, read from the register's occupation. */
export const occupationGroup = (m: PersonLike): string => {
  const o = String(m.occupation || '').trim();
  if (!o) return 'Not recorded';
  if (/student|studying|pursuing|school|college/i.test(o)) return 'Studying';
  if (/homemaker|house ?wife|retired|unemploy|job ?seek|none/i.test(o)) return 'Not working';
  return 'Working';
};

const AGE_BANDS: Array<[string, number, number]> = [
  ['Under 13', 0, 12], ['13 – 17', 13, 17], ['18 – 24', 18, 24], ['25 – 35', 25, 35], ['36 and above', 36, 200]
];

/** Days until the next birthday (0 when it is today), or null with no date of birth. */
const daysToBirthday = (dob: unknown, now: Date): number | null => {
  const d = dateOf(dob);
  if (!d) return null;
  const [, m, day] = d.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  const thisYear = daysUntil(`${now.getFullYear()}-${pad(m)}-${pad(day)}`, now) as number;
  return thisYear >= 0 ? thisYear : daysUntil(`${now.getFullYear() + 1}-${pad(m)}-${pad(day)}`, now);
};

export interface FellowshipRow {
  name: string;
  count: number;
  male: number;
  female: number;
}

export interface AgeChange {
  name: string;
  fellowship: string;
  age: number;
  /** Leaving the fellowship's band, or reaching it. */
  change: 'Ageing out' | 'Joining';
  birthday: string;
  inDays: number;
}

export interface YouthSummary {
  fellowships: number;
  perFellowship: FellowshipRow[];
  /** Distinct register members in at least one fellowship. */
  youth: number;
  ageBands: NamedCount[];
  genders: NamedCount[];
  occupations: NamedCount[];
  studying: number;
  working: number;
  programsThisYear: number;
  months: MonthBucket[];
  byType: NamedTotal[];
  byFellowship: NamedTotal[];
  /** Average attendance over past programmes that recorded one. */
  avgAttendance: number | null;
  /** Budget used by programmes held so far this year (planned ones are left out). */
  budgetThisYear: number;
  upcoming: ProgramLike[];
  /** Members whose birthday in the next `window` days moves them out of (or into) a fellowship. */
  ageChanges: AgeChange[];
}

export const youthSummary = (
  groups: FellowshipLike[], people: PersonLike[], programs: ProgramLike[], now: Date = new Date(), window = 90
): YouthSummary => {
  const live = groups.filter((g) => !isVoid(g));
  const alive = people.filter((m) => /^alive$/i.test(String(m.status || 'Alive').trim()));
  const progs = programs.filter((p) => !isVoid(p));
  const today = isoOf(now);
  const past = progs.filter((p) => (dateOf(p.date) || '') <= today);
  const year = inYear(progs, (p) => p.date, now.getFullYear());

  const inAny = new Set<PersonLike>();
  const perFellowship = live.map((g) => {
    const members = alive.filter((m) => fitsFellowship(g, m, now));
    members.forEach((m) => inAny.add(m));
    return {
      name: String(g.name || '').trim() || 'Unnamed',
      count: members.length,
      male: members.filter((m) => m.gender === 'Male').length,
      female: members.filter((m) => m.gender === 'Female').length
    };
  }).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const youth = Array.from(inAny);
  const ageOf = (m: PersonLike) => yearsSince(m.dateOfBirth, now);
  const ageBands = AGE_BANDS
    .map(([name, lo, hi]) => ({ name, count: youth.filter((m) => { const a = ageOf(m); return a !== null && a >= lo && a <= hi; }).length }))
    .filter((b) => b.count > 0);
  const occupations = countBy(youth, occupationGroup);

  const withCount = past.filter((p) => amountOf(p.attendance) > 0);

  // A birthday inside the window moves a member across a fellowship's age limit.
  const ageChanges: AgeChange[] = [];
  live.forEach((g) => {
    const from = bound(g.ageFrom); const to = bound(g.ageTo);
    alive.forEach((m) => {
      const age = ageOf(m);
      const inDays = daysToBirthday(m.dateOfBirth, now);
      if (age === null || inDays === null || inDays > window) return;
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + inDays);
      const row = { name: String(m.name || '-'), fellowship: String(g.name || '-'), age, birthday: isoOf(next), inDays };
      if (to !== null && age === to && fitsFellowship(g, m, now)) ageChanges.push({ ...row, change: 'Ageing out' });
      else if (from !== null && age === from - 1 && fitsFellowship({ ...g, ageFrom: null, ageTo: null }, m, now)) {
        ageChanges.push({ ...row, change: 'Joining' });
      }
    });
  });
  ageChanges.sort((a, b) => a.inDays - b.inDays || a.name.localeCompare(b.name));

  return {
    fellowships: live.length,
    perFellowship,
    youth: youth.length,
    ageBands,
    genders: countBy(youth, (m) => m.gender),
    occupations,
    studying: occupations.find((o) => o.name === 'Studying')?.count || 0,
    working: occupations.find((o) => o.name === 'Working')?.count || 0,
    programsThisYear: year.length,
    months: lastMonths(past, (p) => p.date, 12, now, (p) => p.budget),
    byType: totalBy(progs, (p) => p.progType, (p) => p.budget),
    byFellowship: totalBy(progs, (p) => p.fellowshipName || FELLOWSHIP_FALLBACK, (p) => p.budget),
    avgAttendance: withCount.length
      ? Math.round(withCount.reduce((n, p) => n + amountOf(p.attendance), 0) / withCount.length)
      : null,
    budgetThisYear: sumOf(year.filter((p) => (dateOf(p.date) || '') <= today), (p) => p.budget),
    upcoming: upcoming(progs, (p) => p.date, 60, now),
    ageChanges
  };
};
